---
title: Introduction
sidebar_position: 1
description: What Planet Area Monitoring is, the core concepts, prerequisites and access.
---

# Introduction

## What is Planet Area Monitoring Service?

The **Planet Area Monitoring Service (Planet AMS)** is a cloud-native solution designed to monitor and verify activities over time across agricultural and land-use areas. The service combines public Earth observation data from Sentinel-1 and Sentinel-2 with high-resolution Planet imagery, including Analysis-Ready PlanetScope (ARPS) and Planetary Variables.

Planet AMS transforms raw satellite imagery into two key types of analytical data:

- **Signals**: Time-series analysis aggregated per FOI from satellite observations, capturing how field conditions change throughout the season
- **Markers**: Predictions of phenomena such as bare soil, crop classification, and mowing events, derived from signal phenology patterns

These data points are presented through the **Area Monitoring Application (AM App)**, a specialized web application that provides an intuitive interface combining imagery, signals, markers, evaluations, decisions (traffic-light indicators), and auxiliary data to facilitate instant field verification and compliance checking.

![AM App](../imgs/am-app.png)

## Key Concepts

Before using the AM App, it's important to understand these core concepts:

### Scope

A **Scope** defines the geographical data and temporal intervals available for monitoring. When you select a scope, you're selecting:

- A specific geographic region
- A set of Features of Interest (FOIs) within that region
- Time ranges for which data is available
- Which signals and markers are calculated

Scope selection restricts all subsequent filter options and determines what data you can access.

### Feature of Interest (FOI)

A **Feature of Interest (FOI)** is the fundamental geometric element being monitored in the system. In most cases, an FOI represents an agricultural field or land parcel. Each FOI has:

- A unique identifier
- Geographic boundaries (polygon geometry)
- Associated attributes (crop type, land use, area, etc.)

### Signals

**Signals** are time-series measurements derived from satellite observations. For each FOI, satellite data is processed to calculate values such as:

- Bands reflectance (e.g., red, green, blue, near-infrared)
- Vegetation indices (NDVI, EVI)
- Moisture content
- Chlorophyll levels
- Other spectral measurements

Signals provide continuous monitoring data that shows how field conditions change over time.

### Markers

**Markers** are predictive measurements that indicate specific management patterns or field characteristics. Examples include:

- Bare soil detection
- Crop classification (identifying which crop is growing)
- Mowing event detection
- Homogeneity assessment

Markers are derived from signal phenology patterns and help identify key agricultural activities and field states.

There are two specialised marker types built on top of base markers:

- **Evaluation Markers** use signals and markers to answer a specific yes/no question, for example "Was this field mowed in July?".
- **Decision Markers** combine multiple Evaluation Markers in a configurable decision tree to produce a traffic-light assessment aligned with an organisation's business rules or compliance criteria.

### Marker Context

A **Marker Context** handles multiple marker calculations that may be generated throughout a growing season. You can switch between different marker contexts to compare different calculation scenarios or time periods (e.g. monthly iterations).

## System Prerequisites

To use the AM App, you need:

**Web Browser**:

- Modern web browser (Chrome, Firefox, Edge, Safari)
- JavaScript enabled

**Internet Connection**:

- Stable internet connection
- Recommended: Broadband connection for optimal performance

**Account Access**:

- Planet account OR dedicated Area Monitoring Service credentials
- Appropriate scope permissions (configured by administrator)

**Display**:

- Minimum screen resolution: 1366x768 recommended
- Not supported for small screens (mobile phones)

## Access Rights and Permissions

Access to data in the AM App is controlled by scope permissions:

- The `DEMO_FR23` scope is enabled by default for all users with a standard Planet account. This demonstration scope allows you to explore the application's features.
- To access additional or project-specific scopes, you must request access.

Scope permissions determine:

- Which FOIs you can view
- What time ranges are available
- Which signals and markers are calculated
- Access to administrative features (if applicable)
